import React, { useEffect, useState, useMemo } from 'react';
import { Pencil, Trash2, UserPlus, MoreVertical, Search, Plus } from 'lucide-react';
import useComponentVisible from '../UI/ComponentVisible';
import { useUserContext } from '../../contexts/UserContext';
import { getAllOrganizations, getOrgLevels } from '../../utils/APIs';
import OrganizationModal from './OrganizationModal';
import DeleteOrganizationModal from './DeleteOrganizationModal';
import AddOrInviteUserModal from './AddOrInviteUser';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { Button, Input, AgTableCard } from '../UI';

const ManageOrganizations = () => {
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [loading, setLoading] = useState(false);
    const [orgLevels, setOrgLevels] = useState([]);
    const [selectedAction, setSelectedAction] = useState('');
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const { userData } = useUserContext();    
    const { content } = useContentfulContext();
    const [gridApi, setGridApi] = useState(null);

    const getOrganizations = async () => {
        try {
            setLoading(true);
            const res = await getAllOrganizations(userData?.token, userData?.selectedOrganization?.org_token);
            if (res.status) {
                setOrganizations(res.data);
            }
        } catch (error) {
            console.error("Error fetching organizations:", error);
        } finally {
            setLoading(false);
        }
    };

    const getorgs = async () => {
        try {
            const res = await getOrgLevels(userData.token, userData?.selectedOrganization?.org_token)
            console.log(res)
            if (res.status) {
                setOrgLevels(res.data)
            }
        } catch (error) {
            console.log("error", error)
        }
    }
    
    useEffect(() => {
        getOrganizations();
        getorgs()
    }, []);

    const colDefs = [
        {
            headerName: content.ORGANIZATION.table_headers.name,
            field: "name",
            filter: true,
            cellRenderer: ({ data }) => (
                <button
                    type="button"
                    className={`font-medium text-left ${data?.level == "0" ? "text-stride-heading cursor-default" : "text-primary hover:text-primary-hover hover:underline cursor-pointer"}`}
                    onClick={() => {
                        if (data?.level == "0") return;
                        setSelectedOrganization(data);
                        setSelectedAction("edit");
                    }}
                >
                    {data.name}
                </button>
            ),
            suppressMovable: true
        },
        {
            headerName: content.ORGANIZATION.table_headers.display_name,
            field: "display_name",
            cellRenderer: ({ data }) => (
                <span className="text-xs text-stride-body font-medium">{data?.display_name || "—"}</span>
            ),
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: content.ORGANIZATION.table_headers.level,
            field: "level",
            cellRenderer: ({ data }) => (
                <span className="stride-badge-pill bg-purple-50 text-purple-700 border border-purple-200 text-xs font-semibold">
                    Level {data?.level}
                </span>
            ),
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: "Actions",
            field: "actions",
            width: 140,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: ({ data }) => {
                const canModify = data?.level != "0";
                return (
                    <div className="stride-table-actions">
                        {canModify && (
                            <button
                                type="button"
                                title="Edit Organization"
                                className="stride-table-btn stride-table-btn-edit"
                                onClick={() => {
                                    setSelectedOrganization(data);
                                    setSelectedAction("edit");
                                }}
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                        )}
                        <button
                            type="button"
                            title="Invite User"
                            className="stride-table-btn hover:text-primary hover:border-primary/40"
                            onClick={() => {
                                setSelectedOrganization(data);
                                setSelectedAction('invite-user');
                            }}
                        >
                            <UserPlus className="w-3.5 h-3.5 text-primary" />
                        </button>
                        {canModify && (
                            <button
                                type="button"
                                title="Delete Organization"
                                className="stride-table-btn stride-table-btn-delete"
                                onClick={() => {
                                    setSelectedOrganization(data);
                                    setSelectedAction('delete');
                                }}
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        )}
                        <button
                            type="button"
                            className="stride-table-btn actions"
                            title="More options"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsComponentVisible(!isComponentVisible);
                                setSelectedOrganization(data);
                                const buttonRect = e.currentTarget.getBoundingClientRect();
                                setMenuPosition({
                                    top: buttonRect.bottom + window.scrollY,
                                    left: buttonRect.left + window.scrollX
                                });
                            }}
                        >
                            <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                    </div>
                );
            },
        }
    ];

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 120,
            columnLimits: [
                { colId: "name", minWidth: 220 },
                { colId: "display_name", minWidth: 200 },
                { colId: "level", minWidth: 120 },
                { colId: "actions", minWidth: 140 }
            ],
        };
    }, []);

    const onFilterTextBoxChanged = (event) => {
        const filterValue = (event.target.value || '').toLowerCase();
        gridApi.setFilterModel({
            name: { type: 'contains', filter: filterValue }
        });
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    return (
        <div className="stride-page-shell">
            <main id="main" className="flex flex-col space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <span className="stride-eyebrow">Administration</span>
                        <div className="flex items-baseline gap-3">
                            <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                                {content.ORGANIZATION.title}
                            </h1>
                            <span className="text-xs text-stride-muted font-normal">
                                {organizations.length} {organizations.length === 1 ? 'organization' : 'organizations'}
                            </span>
                        </div>
                    </div>

                    <Button
                        variant="primary"
                        icon={<Plus className="w-4 h-4" />}
                        onClick={() => {
                            setSelectedAction("create");
                            setSelectedOrganization(null);
                        }}
                    >
                        {content.ORGANIZATION.cta_text.create_org}
                    </Button>
                </div>

                <AgTableCard
                    header={
                        <div className="w-full sm:w-80">
                            <Input
                                placeholder="Search organizations..."
                                onChange={onFilterTextBoxChanged}
                                prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                clearable
                            />
                        </div>
                    }
                    tableId="manage-organizations-table"
                    loading={loading}
                    rowData={organizations}
                    columnDefs={colDefs}
                    autoSizeStrategy={autoSizeStrategy}
                    onGridReady={onGridReady}
                >
                    {isComponentVisible && (
                        <div
                            ref={ref}
                            className="absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1"
                            style={{ top: menuPosition.top, left: Math.max(10, menuPosition.left - 180) }}
                        >
                            <div className="py-1">
                                {selectedOrganization?.level != "0" && (
                                    <a
                                        href="#"
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsComponentVisible(false);
                                            setSelectedAction('edit');
                                        }}
                                    >
                                        {content.ORGANIZATION.more_info.edit}
                                    </a>
                                )}
                                <a
                                    href="#"
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setSelectedAction('invite-user');
                                    }}
                                >
                                    {content.ORGANIZATION.more_info.invite_user}
                                </a>
                                {selectedOrganization?.level != "0" && (
                                    <a
                                        href="#"
                                        className="text-rose-600 block px-4 py-2 text-sm hover:bg-gray-100"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsComponentVisible(false);
                                            setSelectedAction('delete');
                                        }}
                                    >
                                        {content.ORGANIZATION.more_info.delete}
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </AgTableCard>
            </main>
            {
                selectedAction === "create" && <OrganizationModal setSelectedAction={setSelectedAction} getOrganizations={getOrganizations} orgLevels={orgLevels} />
            }
            {
                selectedAction === "edit" && <OrganizationModal setSelectedAction={setSelectedAction} selectedOrganization={selectedOrganization} getOrganizations={getOrganizations} orgLevels={orgLevels} />
            }
            {
                selectedAction === "delete" && <DeleteOrganizationModal setSelectedAction={setSelectedAction} selectedOrganization={selectedOrganization} setSelectedOrganization={setSelectedOrganization} getOrganizations={getOrganizations} />
            }
            {
                selectedAction === "invite-user" && <AddOrInviteUserModal setSelectedAction={setSelectedAction} selectedOrganization={selectedOrganization} getOrganizations={getOrganizations} />
            }
        </div>
    );
};

export default ManageOrganizations;
